import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, FileText } from "lucide-react";
import { api } from "@/lib/api";
import "@/styles/print.css";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};

const formatChicagoCitation = (citation, index) => {
  const parts = [];
  
  if (citation.author) parts.push(citation.author);
  if (citation.title) parts.push(`"${citation.title}"`);
  if (citation.publisher) parts.push(citation.publisher);
  if (citation.date_published) parts.push(citation.date_published);
  if (citation.page_or_timecode) parts.push(`p. ${citation.page_or_timecode}`);
  
  return (
    <div key={citation.id} className="print-footnote">
      <span className="print-footnote-num">{index + 1}.</span>{" "}
      {parts.join(", ") || citation.title || "Untitled source"}.
      {citation.url && ` Available at: ${citation.url}`}
    </div>
  );
};

const PrintClaimPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [claim, setClaim] = useState(null);
  const [citations, setCitations] = useState([]);
  const [correctionsCount, setCorrectionsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasPrinted = useRef(false);
  const autoPrint = searchParams.get("auto") === "true";

  const loadData = useCallback(async () => {
    try {
      // Load claim
      const claimRes = await api.get(`/claims/${id}`);
      setClaim(claimRes.data);
      
      // Load citations for this claim
      const citRes = await api.get(`/citations?claim_id=${id}`);
      setCitations(citRes.data);
      
      // Get corrections count
      try {
        const logRes = await api.get(`/corrections-log?item_type=claim&item_id=${id}`);
        setCorrectionsCount(logRes.data.length);
      } catch {
        setCorrectionsCount(0);
      }
      
    } catch (err) {
      setError("Failed to load claim");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // Auto-print after content loads
    if (!loading && claim && autoPrint && !hasPrinted.current) {
      hasPrinted.current = true;
      setTimeout(() => window.print(), 500);
    }
  }, [loading, claim, autoPrint]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="print-layout">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (error || !claim) {
    return (
      <div className="print-layout">
        <div className="text-center py-20 text-red-600">{error || "Claim not found"}</div>
      </div>
    );
  }

  const printDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });

  const sourceUrl = `${window.location.origin}/claims/${id}`;

  return (
    <div className="print-layout" data-testid="print-claim-page">
      {/* Print Controls (hidden in print) */}
      <div className="print-controls no-print">
        <div className="flex items-center gap-4">
          <Link to={`/claims/${id}`} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4" />
            Back to Site
          </Link>
          <span className="text-gray-400">|</span>
          <span className="text-sm text-gray-600">
            <FileText className="w-4 h-4 inline mr-1" />
            Claim Document
          </span>
        </div>
        <button onClick={handlePrint} data-testid="print-btn">
          <Printer className="w-4 h-4 inline mr-2" />
          Print Document
        </button>
      </div>
      
      {/* Print Header */}
      <div className="print-header">
        <h1>Elvis Aloha 1973 Historical Archive</h1>
        <div className="print-meta">
          Documented Claim
          <br />
          Printed: {printDate} | Source: {sourceUrl}
        </div>
      </div>
      
      {/* Document Content */}
      <div className="print-section-header">
        Claim Documentation
      </div>
      
      {/* Claim Text */}
      <div className="print-entry-title">
        {claim.claim_text}
      </div>
      
      {/* Status */}
      <div className="print-status">
        <strong>Verification Status:</strong> {claim.claim_status || "Unverified"}
      </div>
      
      {/* Summary */}
      {claim.summary && (
        <div className="print-fact-section print-no-break">
          <div className="print-fact-label">Summary</div>
          <div className="print-fact-content print-narrative">
            {claim.summary.split("\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      )}
      
      {/* Detail */}
      {claim.detail && (
        <div className="print-fact-section print-no-break">
          <div className="print-fact-label">Detailed Analysis</div>
          <div className="print-fact-content print-narrative">
            {claim.detail.split("\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      )}
      
      {/* Public Notes */}
      {claim.public_notes && (
        <div className="print-fact-section print-no-break">
          <div className="print-fact-label">Research Notes</div>
          <div className="print-fact-content print-narrative">
            {claim.public_notes.split("\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      )}
      
      {/* Topic Tags */}
      {claim.topic_tags && claim.topic_tags.length > 0 && (
        <div className="print-fact-section">
          <div className="print-fact-label">Topics</div>
          <div className="print-fact-content">
            {claim.topic_tags.join(", ")}
          </div>
        </div>
      )}
      
      {/* Citations */}
      {citations.length > 0 && (
        <div className="print-footnotes print-no-break">
          <div className="print-footnotes-title">Supporting Evidence</div>
          {citations.map((citation, i) => formatChicagoCitation(citation, i))}
        </div>
      )}
      
      {/* Revision History Summary */}
      <div className="print-revision">
        <strong>Document History</strong>
        <br />
        Claim ID: {claim.id}
        <br />
        Created: {formatDate(claim.created_at)}
        <br />
        Last Updated: {formatDate(claim.updated_at || claim.created_at)}
        <br />
        Total Revisions: {correctionsCount}
      </div>
      
      {/* Print Footer (shown in print only via CSS) */}
      <div className="print-footer">
        Elvis Aloha 1973 Historical Archive | {sourceUrl}
      </div>
    </div>
  );
};

export default PrintClaimPage;
